import { useEffect, useMemo, useState } from 'react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type { CompanyContextProfile, CompanyContextResponse, CompanyContextUpdatePayload, IndustryBenchmarkProfileResponse, ServerApiClient } from '../../../data/ServerApiClient'
import { CfoInteriorPage } from './CfoInteriorPage'
import { CfoDepthSection } from './CfoDepthSection'
import { CfoAiRead } from './CfoPagePrimitives'
import { buildCfoDuckDbOpts } from './cfoDuckDb'
import { CFO_SURFACES } from './cfoTelemetry'
import { useCfoBootstrap } from './useCfoBootstrap'
import type { CfoNavProfile } from './cfoNav'
import type { CfoRuntimeProfile } from './cfoRuntimeProfile'

const SURFACE = CFO_SURFACES.advanced

export type CfoAdvancedPageProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  onNavSelect: (navId: string) => void
  activeView?: string | null
  onOpenClassic: () => void
  /** Escape-hatch a specific Advanced tool (layout / adjustments / gl_detail)
   * into its classic legacy surface. */
  onOpenClassicTool: (navId: string) => void
  classicEscapes?: boolean
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
}

const TOOLS: Array<{ navId: string; title: string; body: string; meta: string }> = [
  {
    navId: 'layout',
    title: 'Layout & structure',
    body: 'Rename, regroup, and rearrange the statement presentation. The presentation layer — no data is changed.',
    meta: 'Presentation layer · no data changed',
  },
  {
    navId: 'adjustments',
    title: 'Adjustments / JEs',
    body: 'Post presentational journal entries to prior or current periods. Reclass & accruals, fully audited.',
    meta: 'Reclass & accruals · fully audited',
  },
  {
    navId: 'gl_detail',
    title: 'GL detail',
    body: 'Drill any number down to its general-ledger transactions. Transaction level, traced to source.',
    meta: 'Transaction level · traces to source',
  },
]

export function CfoAdvancedPage({
  appApi,
  workerClient = null,
  workbookId,
  activeView = null,
  onNavSelect,
  onOpenClassic,
  onOpenClassicTool,
  classicEscapes = false,
  navProfile = 'full',
  runtimeProfile,
}: CfoAdvancedPageProps) {
  const duckDbOpts = useMemo(() => buildCfoDuckDbOpts(workbookId), [workbookId])
  const { bootstrap, cacheState, bootstrapLoading } = useCfoBootstrap(appApi, workbookId)
  const isQuickBooksProvider = bootstrap?.accounting_provider === 'quickbooks'
  const showingSettings = activeView === 'settings'
  // Bumped by CompanyContextSettings after a Save re-search, so the sibling industry section
  // re-runs its web detection against the freshly-saved company context.
  const [industryResearchNonce, setIndustryResearchNonce] = useState(0)

  return (
    <CfoInteriorPage
      appApi={appApi}
      workerClient={workerClient}
      workbookId={workbookId}
      navId={showingSettings ? 'settings' : 'layout'}
      activeSection={SURFACE}
      runtimeProfile={runtimeProfile}
      eyebrow="Advanced"
      title={showingSettings ? 'AI company context settings' : 'Power tools, when you need them'}
      lede={showingSettings
        ? 'Review the company facts FuseGrid scraped or inferred from web search, then edit anything the AI should treat differently.'
        : (
          <>
            “You’ll rarely come here — the brief and the copilot handle the day-to-day. <em>But when you want full control, it’s all here.</em>”
          </>
        )}
      crumb={
        <>
          <b>Advanced</b> · {showingSettings ? 'Settings' : 'Power tools'}
        </>
      }
      asOfLabel="—"
      cacheState={cacheState}
      pageLoading={bootstrapLoading}
      pageLoadingLabel="Checking CFO analytics readiness…"
      copilotSubtitle="ready to drive the power tools"
      copilotPlaceholder="Ask about Advanced…"
      copilotIntro="These are the expert surfaces. Tell me what to restructure, accrue, or trace, and I’ll draft it for your approval."
      askPlaceholder="Ask to restructure, post, or trace…"
      suggestedPrompts={['Regroup opex by department', 'Accrue June’s unbilled time', 'Show the GL behind legal services']}
      duckDbOpts={duckDbOpts}
      onNavSelect={onNavSelect}
      navProfile={navProfile}
    >
      {() => (
        <>
          {showingSettings ? null : (
            <>
              <div className="section-title rise">
                <h2 className="ff-guide">Three expert tools</h2>
                <span className="meta">for when you want to shape the numbers yourself</span>
              </div>
              <div className="cards">
                {TOOLS.map((tool) => (
                  <article className="card edge good rise" key={tool.navId}>
                    <div className="body">
                      <div className="grow">
                        <h3>{tool.title}</h3>
                        <p>{tool.body}</p>
                      </div>
                    </div>
                    {classicEscapes ? (
                      <div className="actions">
                        <button type="button" className="btn primary" onClick={() => onOpenClassicTool(tool.navId)}>
                          Open {tool.title}
                        </button>
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>

              <CfoDepthSection
                title="GL detail, one tap down"
                meta="Native ledger chrome"
                showLabel="Show the GL detail"
                surface={SURFACE}
                workbookId={workbookId}
              >
                {() => (
                  <>
                    <div className="cfo-shell-notice">
                      The governed ledger is available through the same paged, source-traceable GL detail surface used by every accounting provider.
                      <button type="button" className="btn" onClick={() => onOpenClassicTool('gl_detail')}>
                        Open GL detail
                      </button>
                    </div>
                    <CfoAiRead>
                      GL detail stays transaction-level and source-traceable, and loads bounded pages from the governed company ledger.
                    </CfoAiRead>
                  </>
                )}
              </CfoDepthSection>
            </>
          )}

          {isQuickBooksProvider ? (
            <>
              <IndustryBenchmarkSettings appApi={appApi} workbookId={workbookId} researchSignal={industryResearchNonce} />
              <CompanyContextSettings
                appApi={appApi}
                workbookId={workbookId}
                onRequestIndustryResearch={() => setIndustryResearchNonce((n) => n + 1)}
              />
            </>
          ) : (
            <section className="rise" aria-label="Accounting provider AI policy" style={{ marginTop: 'var(--s7)' }}>
              <div className="cfo-shell-notice">
                Runtime generative commentary and cross-company learning are disabled for this provider. Forecasts, ratios, statements, cash planning, and valuation continue to use this company’s reconciled data only.
              </div>
            </section>
          )}
        </>
      )}
    </CfoInteriorPage>
  )
}

type CompanyContextDraft = {
  website_url: string
  website_title: string
  business_summary: string
  inferred_industry: string
  geography: string
  ai_context_notes: string
  customer_segments: string[]
  services: string[]
}

const emptyCompanyContextDraft = (): CompanyContextDraft => ({
  website_url: '',
  website_title: '',
  business_summary: '',
  inferred_industry: '',
  geography: '',
  ai_context_notes: '',
  customer_segments: [],
  services: [],
})

/** Coerce a stored field (array, JSON string, or scalar) into a clean list of strings. */
const toStringList = (value: any): string[] => {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean)
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return []
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) return parsed.map((item) => String(item).trim()).filter(Boolean)
    } catch {
      /* not JSON — treat as a single entry */
    }
    return [trimmed]
  }
  return []
}

const humanizeKey = (key: string) =>
  key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())

/** Turn the public-signals object into readable label + bullet groups. */
const toSignalGroups = (value: any): Array<{ label: string; items: string[] }> => {
  let obj: any = value
  if (typeof value === 'string') {
    try {
      obj = JSON.parse(value)
    } catch {
      return value.trim() ? [{ label: 'Signals', items: [value.trim()] }] : []
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return Array.isArray(obj) ? [{ label: 'Signals', items: toStringList(obj) }] : []
  }
  const groups: Array<{ label: string; items: string[] }> = []
  for (const [key, raw] of Object.entries(obj)) {
    const items = toStringList(raw)
    if (items.length) groups.push({ label: humanizeKey(key), items })
  }
  return groups
}

/** How a stored context/industry row got its values, in the user's words. */
export const settingsSourceLabel = (source: string | null | undefined): string => {
  switch ((source || '').trim()) {
    case 'user_edit':
      return 'Your edits'
    case 'user_override':
      return 'Your choice'
    case 'web_search':
    case 'heuristic':
      return 'Auto-detected'
    // Authored fixtures for the synthetic demo companies — never scraped.
    case 'demo_seed':
      return 'Demo company profile'
    default:
      return source || 'Not set'
  }
}

/**
 * The benchmark catalog is coarser than a company's own NAICS (it prefix-matches:
 * 722511 benchmarks against the 722 entry), so a stored code often has no exact
 * option. Keep it as its own option instead of letting the <select> fall through
 * to whatever happens to be first — that showed "All industries" for a company
 * benchmarked against Food Services, and a Save would have stored that.
 */
export const industryOptionsForSelect = (
  options: Array<{ naics_code: string; industry_title: string }>,
  selectedCode: string,
  selectedTitle?: string | null,
): Array<{ naics_code: string; industry_title: string }> => {
  const code = (selectedCode || '').trim()
  if (!code || options.some((option) => option.naics_code === code)) return options
  return [{ naics_code: code, industry_title: selectedTitle?.trim() || 'Selected industry' }, ...options]
}

const draftFromCompanyContext = (context: CompanyContextProfile | null | undefined): CompanyContextDraft => ({
  website_url: context?.website_url || '',
  website_title: context?.website_title || '',
  business_summary: context?.business_summary || '',
  inferred_industry: context?.inferred_industry || '',
  geography: context?.geography || '',
  ai_context_notes: context?.ai_context_notes || '',
  customer_segments: toStringList(context?.customer_segments_json),
  services: toStringList(context?.services_json),
})

function ListEditor({
  label,
  hint,
  placeholder,
  items,
  onChange,
}: {
  label: string
  hint: string
  placeholder: string
  items: string[]
  onChange: (next: string[]) => void
}) {
  const [entry, setEntry] = useState('')
  const add = () => {
    const value = entry.trim()
    if (!value) return
    if (!items.some((item) => item.toLowerCase() === value.toLowerCase())) {
      onChange([...items, value])
    }
    setEntry('')
  }
  return (
    <div className="fg-list-editor">
      <div className="fg-list-editor-head">
        <span>{label}</span>
        <em>{hint}</em>
      </div>
      <div className="fg-chip-list">
        {items.length === 0 ? (
          <p className="fg-list-empty">Nothing added yet.</p>
        ) : (
          items.map((item, index) => (
            <span className="fg-chip" key={`${item}-${index}`}>
              {item}
              <button
                type="button"
                aria-label={`Remove ${item}`}
                onClick={() => onChange(items.filter((_, idx) => idx !== index))}
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>
      <div className="fg-list-add">
        <input
          value={entry}
          placeholder={placeholder}
          onChange={(event) => setEntry(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              add()
            }
          }}
        />
        <button type="button" className="btn" onClick={add} disabled={!entry.trim()}>
          Add
        </button>
      </div>
    </div>
  )
}

function CompanyContextSettings({
  appApi,
  workbookId,
  onRequestIndustryResearch,
}: {
  appApi: ServerApiClient
  workbookId: string | null
  onRequestIndustryResearch?: () => void
}) {
  const [response, setResponse] = useState<CompanyContextResponse | null>(null)
  const [draft, setDraft] = useState<CompanyContextDraft>(() => emptyCompanyContextDraft())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [status, setStatus] = useState('')
  const [error, setError] = useState('')
  // Transient steering for the web search — not persisted; only used to focus the next refresh.
  const [searchHint, setSearchHint] = useState('')

  useEffect(() => {
    const wb = workbookId?.trim()
    if (!wb) {
      setResponse(null)
      setDraft(emptyCompanyContextDraft())
      return
    }
    let cancelled = false
    setLoading(true)
    setError('')
    appApi.getQuickBooksCompanyContext(wb)
      .then((next) => {
        if (cancelled) return
        setResponse(next)
        setDraft(draftFromCompanyContext(next.context))
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || 'Could not load company AI context.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appApi, workbookId])

  const updateDraft = <K extends keyof CompanyContextDraft>(key: K, value: CompanyContextDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }))
    if (error) setError('')
    if (status) setStatus('')
  }

  // The web-search payload: the whole form as authoritative input. Anything the user filled in is
  // kept and used to focus the search; only blank fields get (re)filled. website_url is the anchor.
  const buildDetectPayload = () => ({
    persist: true as const,
    website_url: draft.website_url.trim() || null,
    website_title: draft.website_title.trim() || null,
    business_summary: draft.business_summary.trim() || null,
    inferred_industry: draft.inferred_industry.trim() || null,
    geography: draft.geography.trim() || null,
    ai_context_notes: draft.ai_context_notes.trim() || null,
    customer_segments: draft.customer_segments.length ? draft.customer_segments : null,
    services: draft.services.length ? draft.services : null,
    search_hint: searchHint.trim() || null,
  })

  const save = async () => {
    const wb = workbookId?.trim()
    if (!wb) return
    setSaving(true)
    setError('')
    setStatus('')
    try {
      // 1. Persist exactly what the user typed first, so their edits survive even if the search fails.
      const payload: CompanyContextUpdatePayload = {
        website_url: draft.website_url,
        website_title: draft.website_title,
        business_summary: draft.business_summary,
        inferred_industry: draft.inferred_industry,
        geography: draft.geography,
        ai_context_notes: draft.ai_context_notes,
        customer_segments_json: draft.customer_segments,
        services_json: draft.services,
        // Public signals and user overrides are AI/system-managed; preserve them verbatim.
        public_signals_json: response?.context?.public_signals_json ?? {},
        user_overrides_json: response?.context?.user_overrides_json ?? {},
        sources_json: response?.context?.sources_json || [],
        confidence: response?.context?.confidence ?? null,
        enrichment_source: 'user_edit',
      }
      await appApi.updateQuickBooksCompanyContext(wb, payload)

      // 2. Kick off a fresh web search anchored on the saved input (merge keeps filled fields, fills blanks).
      const next = await appApi.detectQuickBooksCompanyContext(wb, buildDetectPayload())
      setResponse(next)
      setDraft(draftFromCompanyContext(next.context))

      // 3. Re-run the industry benchmark search against the freshly-saved company context.
      onRequestIndustryResearch?.()
      setStatus('Saved — re-running the web + industry search from your inputs. Fields you filled in stay; blanks get filled.')
    } catch (err: any) {
      setError(err?.message || 'Could not save and re-search company context.')
    } finally {
      setSaving(false)
    }
  }

  const refresh = async () => {
    const wb = workbookId?.trim()
    if (!wb) return
    setRefreshing(true)
    setError('')
    setStatus('')
    try {
      const next = await appApi.detectQuickBooksCompanyContext(wb, buildDetectPayload())
      setResponse(next)
      setDraft(draftFromCompanyContext(next.context))
      setStatus('Web context refreshed. Fields you filled in were kept; blank ones were filled from the search.')
    } catch (err: any) {
      setError(err?.message || 'Could not refresh company context from web search.')
    } finally {
      setRefreshing(false)
    }
  }

  // Empty every field so the next Refresh is a fresh search. Non-destructive: only the form is
  // cleared — the saved record is untouched until the user Refreshes or Saves.
  const clearFields = () => {
    const filled =
      Object.values(draft).some((value) => (Array.isArray(value) ? value.length > 0 : Boolean(value))) ||
      searchHint.trim().length > 0
    if (
      filled &&
      !window.confirm(
        'Clear every field? Nothing is saved yet — your stored context stays until you Refresh or Save. ' +
          'After clearing, type in what you know (at least the correct website), then Refresh to run a fresh search focused on it.',
      )
    ) {
      return
    }
    setDraft(emptyCompanyContextDraft())
    setSearchHint('')
    setError('')
    setStatus('Fields cleared. Type in the correct website (and anything else you know), then Refresh — or Save & re-search — to run a fresh search.')
  }

  const context = response?.context || null
  const signalGroups = useMemo(() => toSignalGroups(context?.public_signals_json), [context?.public_signals_json])

  return (
    <section className="rise" aria-label="AI company context settings" style={{ marginTop: 'var(--s7)' }}>
      <div className="section-title">
        <h2 className="ff-guide">AI company context</h2>
        <span className="meta">the facts the CFO copilot uses to personalize its advice</span>
      </div>
      <div className="panel fg-settings-panel">
        <div className="fg-settings-head">
          <div className="fg-settings-head-text">
            <h3>{response?.company_name || 'QuickBooks company'}</h3>
            <p>
              FuseGrid uses this as directional context for AI explanations and recommendations. QuickBooks numbers stay authoritative; these facts only personalize wording and suggested next questions.
            </p>
          </div>
          <div className="fg-settings-actions">
            <button type="button" className="btn" disabled={!workbookId || loading || refreshing || saving} onClick={clearFields}>
              Clear all
            </button>
            <button type="button" className="btn" disabled={!workbookId || loading || refreshing || saving} onClick={refresh}>
              {refreshing ? 'Searching…' : context ? 'Refresh web context' : 'Search web context'}
            </button>
            <button type="button" className="btn primary" disabled={!workbookId || loading || refreshing || saving} onClick={save}>
              {saving ? 'Saving & searching…' : 'Save & re-search'}
            </button>
          </div>
        </div>

        {loading ? <div className="fg-settings-note">Loading company context…</div> : null}
        {error ? <div className="fg-settings-note is-error">{error}</div> : null}
        {status ? <div className="fg-settings-note is-success">{status}</div> : null}

        <div className="fg-context-group">
          <div className="fg-context-group-head">
            <h4>Focus the web search</h4>
            <p>
              Company names are often shared by several real businesses. When you <b>Refresh</b> or <b>Save &amp; re-search</b>,
              anything you've already filled in below is <b>kept and used to focus the search</b> — set the correct
              <b> Official website</b> and the search treats that exact site as the source of truth. Only the fields you
              leave blank get filled from the search, so to re-run a field, clear it first. Use <b>Clear all</b> (top right)
              to wipe everything and start fresh. Add a note here to disambiguate look-alikes.
            </p>
          </div>
          <label className="fg-context-field">
            <span>Search hint (optional)</span>
            <input
              value={searchHint}
              onChange={(event) => setSearchHint(event.target.value)}
              placeholder="e.g. The agriculture seed company at supra.ag — not the infrastructure firm with the same name"
            />
          </label>
        </div>

        <div className="fg-context-group">
          <div className="fg-context-group-head">
            <h4>Company basics</h4>
            <p>What kind of business this is, and where it operates.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--s4)' }}>
            <label className="fg-context-field">
              <span>Official website</span>
              <input value={draft.website_url} onChange={(event) => updateDraft('website_url', event.target.value)} placeholder="https://…" />
            </label>
            <label className="fg-context-field">
              <span>Website title</span>
              <input value={draft.website_title} onChange={(event) => updateDraft('website_title', event.target.value)} />
            </label>
            <label className="fg-context-field">
              <span>Industry</span>
              <input value={draft.inferred_industry} onChange={(event) => updateDraft('inferred_industry', event.target.value)} placeholder="e.g. Law firm (legal services)" />
            </label>
            <label className="fg-context-field">
              <span>Geography / service area</span>
              <input value={draft.geography} onChange={(event) => updateDraft('geography', event.target.value)} placeholder="e.g. Arizona and Michigan" />
            </label>
          </div>
          <label className="fg-context-field" style={{ marginTop: 'var(--s4)' }}>
            <span>Business summary</span>
            <textarea rows={4} value={draft.business_summary} onChange={(event) => updateDraft('business_summary', event.target.value)} placeholder="A few sentences describing what the business does." />
          </label>
        </div>

        <div className="fg-context-group">
          <div className="fg-context-group-head">
            <h4>What the business sells &amp; who buys it</h4>
            <p>Add or remove items so the AI describes the business in your words.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--s5)' }}>
            <ListEditor
              label="Services / products"
              hint="What the company offers"
              placeholder="Add a service, then press Enter"
              items={draft.services}
              onChange={(next) => updateDraft('services', next)}
            />
            <ListEditor
              label="Customer segments"
              hint="Who the customers are"
              placeholder="Add a segment, then press Enter"
              items={draft.customer_segments}
              onChange={(next) => updateDraft('customer_segments', next)}
            />
          </div>
        </div>

        <div className="fg-context-group">
          <div className="fg-context-group-head">
            <h4>How the AI should tailor advice</h4>
            <p>Guidance the CFO copilot keeps in mind when explaining the numbers.</p>
          </div>
          <label className="fg-context-field">
            <span>Personalization notes</span>
            <textarea rows={3} value={draft.ai_context_notes} onChange={(event) => updateDraft('ai_context_notes', event.target.value)} placeholder="e.g. Track cash flow by practice area and location; call out the impact of the new service lines separately." />
          </label>
        </div>

        {signalGroups.length > 0 ? (
          <div className="fg-context-group">
            <div className="fg-context-group-head">
              {/* Demo companies are fictional: their signals are authored fixtures, so don't
                  claim they came off the web. */}
              {context?.enrichment_source === 'demo_seed' ? (
                <>
                  <h4>Background the AI keeps in mind</h4>
                  <p>How this business makes money and what that implies for its numbers.</p>
                </>
              ) : (
                <>
                  <h4>What FuseGrid noticed from the web</h4>
                  <p>Background signals the AI gathered. These update when you refresh web context.</p>
                </>
              )}
            </div>
            <div className="fg-signals">
              {signalGroups.map((group) => (
                <div className="fg-signal-group" key={group.label}>
                  <h5>{group.label}</h5>
                  <ul>
                    {group.items.map((item, index) => (
                      <li key={`${group.label}-${index}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="fg-context-footer">
          <span>Source<b>{context?.enrichment_source === 'web_search' ? 'Web search' : settingsSourceLabel(context?.enrichment_source)}</b></span>
          {typeof context?.confidence === 'number' ? <span>Confidence<b>{Math.round(context.confidence * 100)}%</b></span> : null}
          {context?.last_web_search_at ? <span>Last refreshed<b>{new Date(context.last_web_search_at).toLocaleDateString()}</b></span> : null}
        </div>
      </div>
    </section>
  )
}

const optionText = (option: { naics_code: string; industry_title: string }): string =>
  `${option.naics_code} · ${option.industry_title}`

function IndustryBenchmarkSettings({
  appApi,
  workbookId,
  researchSignal,
}: {
  appApi: ServerApiClient
  workbookId: string | null
  researchSignal?: number
}) {
  const [profile, setProfile] = useState<IndustryBenchmarkProfileResponse | null>(null)
  const [selectedCode, setSelectedCode] = useState('')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [researching, setResearching] = useState(false)
  const [status, setStatus] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    const wb = workbookId?.trim()
    if (!wb) {
      setProfile(null)
      setSelectedCode('')
      return
    }
    let cancelled = false
    setLoading(true)
    setError('')
    appApi.getQuickBooksIndustryProfile(wb)
      .then((next) => {
        if (cancelled) return
        setProfile(next)
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || 'Could not load the industry benchmark.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appApi, workbookId])

  useEffect(() => {
    const code = profile?.selected?.naics_code || profile?.candidates?.[0]?.naics_code || ''
    setSelectedCode(code)
  }, [profile])

  const selected = profile?.selected || profile?.candidates?.[0] || null

  const options = useMemo(() => {
    // The stored code may be finer-grained than the catalog (it prefix-matches),
    // so seed the list with it before filtering.
    const all = industryOptionsForSelect(profile?.options || [], selectedCode, selected?.industry_title)
    const q = search.trim().toLowerCase()
    const filtered = q
      ? all.filter((option) => option.naics_code.includes(q) || option.industry_title.toLowerCase().includes(q)).slice(0, 80)
      : all.slice(0, 80)
    // Always keep the currently selected option visible so the <select> never
    // silently snaps to a different industry that happens to be first in the list.
    if (selectedCode && !filtered.some((option) => option.naics_code === selectedCode)) {
      const hit = all.find((option) => option.naics_code === selectedCode)
      if (hit) return [hit, ...filtered]
    }
    return filtered
  }, [profile, search, selectedCode, selected?.industry_title])

  const save = async () => {
    const wb = workbookId?.trim()
    const code = selectedCode.trim()
    if (!wb || !code) return
    setSaving(true)
    setError('')
    setStatus('')
    try {
      const next = await appApi.updateQuickBooksIndustryProfile(wb, {
        naics_code: code,
        selection_source: 'user_override',
        confidence: 1,
        evidence_json: { source: 'user_selected_industry' },
      })
      setProfile(next)
      setStatus('Industry benchmark saved. Ratio comparisons now use this industry.')
    } catch (err: any) {
      setError(err?.message || 'Could not save the industry benchmark.')
    } finally {
      setSaving(false)
    }
  }

  const research = async () => {
    const wb = workbookId?.trim()
    if (!wb) return
    setResearching(true)
    setError('')
    setStatus('')
    try {
      const next = await appApi.detectQuickBooksIndustryProfile(wb, { web_search: true, persist: true })
      setProfile(next)
      setStatus('Industry re-detected from web search. Review and adjust if needed.')
    } catch (err: any) {
      setError(err?.message || 'Could not detect the industry from web search.')
    } finally {
      setResearching(false)
    }
  }

  // A company-context Save re-search bumps researchSignal — re-run industry detection so the
  // benchmark reflects the just-saved company facts. The value only increments, so each bump fires
  // exactly once; 0 is the initial mount, which we skip.
  useEffect(() => {
    if (!researchSignal) return
    void research()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [researchSignal])

  const busy = loading || saving || researching
  const catalog = profile?.catalog || null

  return (
    <section className="rise" aria-label="Industry benchmark settings" style={{ marginTop: 'var(--s7)' }}>
      <div className="section-title">
        <h2 className="ff-guide">Industry benchmark</h2>
        <span className="meta">the NAICS industry ratio analysis compares against</span>
      </div>
      <div className="panel fg-settings-panel">
        <div className="fg-settings-head">
          <div className="fg-settings-head-text">
            <h3>{selected ? selected.industry_title : 'No industry selected yet'}</h3>
            <p>
              {selected
                ? <>Ratio analysis benchmarks this company against <b>NAICS {selected.naics_code}</b>. Pick a different industry below if this isn’t the closest match.</>
                : <>Choose the industry whose averages best match this company. Ratio analysis uses it to judge whether each ratio is healthy.</>}
            </p>
          </div>
          <div className="fg-settings-actions">
            <button type="button" className="btn" disabled={!workbookId || busy} onClick={research}>
              {researching ? 'Researching…' : 'Auto-detect from web'}
            </button>
            <button type="button" className="btn primary" disabled={!workbookId || busy || !selectedCode} onClick={save}>
              {saving ? 'Saving…' : 'Save industry'}
            </button>
          </div>
        </div>

        {loading ? <div className="fg-settings-note">Loading industry benchmark…</div> : null}
        {error ? <div className="fg-settings-note is-error">{error}</div> : null}
        {status ? <div className="fg-settings-note is-success">{status}</div> : null}

        <div className="fg-context-group">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--s4)' }}>
            <label className="fg-context-field">
              <span>Search industries</span>
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by name or NAICS code" />
            </label>
            <label className="fg-context-field">
              <span>Industry benchmark</span>
              <select value={selectedCode} onChange={(event) => setSelectedCode(event.target.value)} disabled={!options.length}>
                {options.length === 0 ? <option value="">No matches</option> : null}
                {options.map((option) => (
                  <option key={option.naics_code} value={option.naics_code}>{optionText(option)}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="fg-context-footer">
          {catalog ? <span>Source<b>{catalog.source_label} {catalog.source_year}</b></span> : null}
          {selected ? <span>Match<b>{settingsSourceLabel(selected.selection_source)}</b></span> : null}
          <span className="fg-footer-note">{catalog?.note || 'Aggregate IRS industry averages — directional context, not private-company medians.'}</span>
        </div>
      </div>
    </section>
  )
}

export default CfoAdvancedPage
